import * as m from '@staiter/i18n';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Decimal } from 'decimal.js-light';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader } from '~/components/data';
import { CounterpartyPicker, PeriodCloseBanner } from '~/components/domain';
import { CompanyContextBanner } from '~/components/domain/CompanyContextBanner';
import { Button, DatePicker, Input, Select, Textarea } from '~/components/primitives';
import { type InvoiceCreateBody, createApInvoice, createArInvoice } from '~/lib/api/invoices';
import shared from './_auth._shared.module.css';
import styles from './_auth.invoices.new.module.css';

/**
 * S14-FE.6 — Invoice create wizard (4-step, multi-line editor).
 */

export const Route = createFileRoute('/_auth/invoices/new')({
  validateSearch: (s: Record<string, unknown>) => ({ kind: (s.kind as 'ar' | 'ap') ?? 'ar' }),
  component: InvoiceNewPage,
});

type LineDraft = {
  key: string;
  description_ru: string;
  qty: string;
  uom_code: string;
  unit_price: string;
  vat_rate: '0' | '12' | 'exempt';
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function plusDays(iso: string, d: number): string {
  const t = new Date(iso);
  t.setUTCDate(t.getUTCDate() + d);
  return t.toISOString().slice(0, 10);
}

function computeLineTotals(line: LineDraft) {
  const qty = new Decimal(line.qty || '0');
  const price = new Decimal(line.unit_price || '0');
  const subtotal = qty.times(price);
  const vat =
    line.vat_rate === 'exempt'
      ? new Decimal(0)
      : subtotal.times(new Decimal(line.vat_rate).dividedBy(100));
  return { subtotal, vat, total: subtotal.plus(vat) };
}

function emptyLine(idx: number): LineDraft {
  return {
    key: `line-${idx}-${Date.now()}`,
    description_ru: '',
    qty: '1',
    uom_code: 'pcs',
    unit_price: '0',
    vat_rate: '12',
  };
}

// FE-129 Sprint P+: добавлены подписи шагов (Details / Lines / Preview / Confirm).
function StepIndicator({ current, total }: { current: number; total: number }): JSX.Element {
  const STEP_LABELS = [
    m.invoice_wizard_step1_label(),
    m.invoice_wizard_step2_label(),
    m.invoice_wizard_step3_label(),
    m.invoice_wizard_step4_label(),
  ];
  return (
    <div className={shared.wizardSteps}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={i + 1 === current ? shared.wizardStepChipActive : shared.wizardStepChip}
        >
          {i + 1}. {STEP_LABELS[i] ?? `${i + 1} / ${total}`}
        </span>
      ))}
    </div>
  );
}

function InvoiceNewPage(): JSX.Element {
  const { kind } = Route.useSearch();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [counterpartyId, setCounterpartyId] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(plusDays(today(), 30));
  const [currency, setCurrency] = useState<'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY'>('KZT');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(0)]);

  const totals = useMemo(() => {
    let subtotal = new Decimal(0);
    let vat = new Decimal(0);
    for (const ln of lines) {
      const lt = computeLineTotals(ln);
      subtotal = subtotal.plus(lt.subtotal);
      vat = vat.plus(lt.vat);
    }
    return { subtotal, vat, total: subtotal.plus(vat) };
  }, [lines]);

  const createMut = useMutation({
    mutationFn: (body: InvoiceCreateBody) =>
      kind === 'ar' ? createArInvoice(body) : createApInvoice(body),
    onSuccess: (r) => void navigate({ to: '/invoices/$id', params: { id: r.id } }),
  });

  const canNextStep1 = Boolean(counterpartyId && issueDate && dueDate);
  const canNextStep2 =
    lines.length > 0 &&
    lines.every((ln) => ln.description_ru.trim() !== '' && !new Decimal(ln.qty || '0').isZero());

  const submit = () => {
    const body: InvoiceCreateBody = {
      counterparty_id: counterpartyId,
      issue_date: issueDate,
      due_date: dueDate,
      currency,
      lines: lines.map((ln, i) => {
        const lt = computeLineTotals(ln);
        return {
          line_no: i + 1,
          item_id: null,
          description_ru: ln.description_ru,
          description_kk: null,
          qty: ln.qty,
          uom_code: ln.uom_code as InvoiceCreateBody['lines'][number]['uom_code'],
          unit_price: { amount: ln.unit_price, currency },
          line_subtotal: { amount: lt.subtotal.toString(), currency },
          vat_rate: ln.vat_rate,
          vat_amount: { amount: lt.vat.toString(), currency },
          line_total: { amount: lt.total.toString(), currency },
        };
      }),
      notes_ru: notes || null,
    };
    createMut.mutate(body);
  };

  return (
    <div>
      <PageHeader
        title={kind === 'ar' ? m.phase2_invoices_ar_title() : m.phase2_invoices_ap_title()}
      />
      <CompanyContextBanner />
      <PeriodCloseBanner scope="invoice" />
      <StepIndicator current={step} total={4} />

      {step === 1 && (
        <div>
          {/* FE-289 Pass 66: убран redundant heading «Контрагент» — label primary
              field уже несёт ту же информацию (dual heading + label = noise). */}
          <div className={styles.formGrid2}>
            {/* FE-127 Sprint Q+: CounterpartyPicker primitive (kind-filtered). */}
            <CounterpartyPicker
              label={m.invoice_column_counterparty()}
              required
              kind={kind === 'ar' ? 'customer' : 'supplier'}
              value={counterpartyId}
              onChange={(id) => setCounterpartyId(id)}
            />
            <Select
              label={m.cp_column_currency()}
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY')}
              options={[
                { value: 'KZT', label: 'KZT' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'RUB', label: 'RUB' },
                { value: 'CNY', label: 'CNY' },
              ]}
            />
            <DatePicker
              label={m.invoice_column_issue_date()}
              required
              value={issueDate}
              onChange={(next) => setIssueDate(next ?? today())}
            />
            <DatePicker
              label={m.invoice_column_due_date()}
              required
              value={dueDate}
              onChange={(next) => setDueDate(next ?? plusDays(today(), 30))}
            />
            <Textarea
              label={m.payment_notes_label()}
              placeholder={m.payment_notes_placeholder()}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className={shared.stickyFooter}>
            <Button variant="secondary" onClick={() => navigate({ to: '/invoices/ar' })}>
              {m.common_button_cancel()}
            </Button>
            <Button variant="primary" onClick={() => setStep(2)} disabled={!canNextStep1}>
              {m.common_button_next()}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2 className={shared.h2Sm}>{m.wizard_section_invoice_lines()}</h2>
          {lines.map((ln, idx) => {
            const lt = computeLineTotals(ln);
            return (
              <div key={ln.key} className={styles.lineRow}>
                <Input
                  label="Описание"
                  value={ln.description_ru}
                  onChange={(e) => {
                    const copy = [...lines];
                    copy[idx] = { ...copy[idx]!, description_ru: e.target.value };
                    setLines(copy);
                  }}
                />
                <Input
                  label={m.cogs_column_qty_sold()}
                  value={ln.qty}
                  onChange={(e) => {
                    const copy = [...lines];
                    copy[idx] = { ...copy[idx]!, qty: e.target.value };
                    setLines(copy);
                  }}
                />
                <Input
                  label="Цена"
                  value={ln.unit_price}
                  onChange={(e) => {
                    const copy = [...lines];
                    copy[idx] = { ...copy[idx]!, unit_price: e.target.value };
                    setLines(copy);
                  }}
                />
                <Select
                  label="VAT"
                  value={ln.vat_rate}
                  onChange={(e) => {
                    const copy = [...lines];
                    copy[idx] = {
                      ...copy[idx]!,
                      vat_rate: e.target.value as '0' | '12' | 'exempt',
                    };
                    setLines(copy);
                  }}
                  options={[
                    { value: '0', label: '0%' },
                    { value: '12', label: '12%' },
                    { value: 'exempt', label: 'Exempt' },
                  ]}
                />
                <div className={styles.calcCell}>
                  <div className={styles.calcLabel}>{m.invoice_new_subtotal()}</div>
                  <strong>{lt.subtotal.toString()}</strong>
                </div>
                <div className={styles.calcCell}>
                  <div className={styles.calcLabel}>{m.invoice_new_total()}</div>
                  <strong>{lt.total.toString()}</strong>
                </div>
                <Button
                  variant="ghost"
                  aria-label="Удалить строку"
                  onClick={() => {
                    if (lines.length > 1) setLines(lines.filter((_, i) => i !== idx));
                  }}
                  disabled={lines.length === 1}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            );
          })}
          <Button
            variant="ghost"
            leadingIcon={<Plus size={16} />}
            onClick={() => setLines([...lines, emptyLine(lines.length)])}
          >
            {m.wizard_button_add_invoice_line()}
          </Button>
          <div className={`${styles.actionsRow} ${shared.stickyFooter}`}>
            <Button variant="ghost" onClick={() => setStep(1)}>
              {m.common_button_back()}
            </Button>
            <Button variant="primary" onClick={() => setStep(3)} disabled={!canNextStep2}>
              {m.common_button_next()}
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2 className={shared.h2Sm}>{m.wizard_section_preview()}</h2>
          <div className={styles.summaryCard}>
            <div>
              {m.invoice_column_counterparty()}: <strong>{counterpartyId.slice(0, 8)}</strong>
            </div>
            <div>
              {m.invoice_column_issue_date()}: <strong>{issueDate}</strong>
            </div>
            <div>
              {m.invoice_column_due_date()}: <strong>{dueDate}</strong>
            </div>
            <div>
              {m.wizard_summary_invoice_lines_count()} <strong>{lines.length}</strong>
            </div>
            <div className={styles.summaryDivider}>
              Subtotal:{' '}
              <strong className={shared.tabular}>
                {totals.subtotal.toString()} {currency}
              </strong>
            </div>
            <div>
              VAT:{' '}
              <strong className={shared.tabular}>
                {totals.vat.toString()} {currency}
              </strong>
            </div>
            <div>
              {m.invoice_column_total()}:{' '}
              <strong data-testid="invoice-new-total" className={shared.tabular}>
                {totals.total.toString()} {currency}
              </strong>
            </div>
          </div>
          <div className={`${styles.actionsRowFlat} ${shared.stickyFooter}`}>
            <Button variant="ghost" onClick={() => setStep(2)}>
              {m.common_button_back()}
            </Button>
            <Button variant="primary" onClick={() => setStep(4)}>
              {m.common_button_next()}
            </Button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <h2 className={shared.h2Sm}>{m.wizard_section_confirm()}</h2>
          <p>
            Создать {kind === 'ar' ? 'AR invoice' : 'AP invoice'} на{' '}
            <strong>
              {totals.total.toString()} {currency}
            </strong>
            ?
          </p>
          <div className={`${styles.actionsRowFlat} ${shared.stickyFooter}`}>
            <Button variant="ghost" onClick={() => setStep(3)}>
              {m.common_button_back()}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              loading={createMut.isPending}
              disabled={totals.total.isZero()}
            >
              {m.common_button_create()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
